import Router from "next/router";
import {
  Button,
  ButtonProps,
  Link,
  TagLabel,
  Text,
} from "@zapier/design-system";
import { addParamsToUrl } from "@zapier/url-utils";
import { Currency, FeatureListing, TooltipPosition } from "@/types";
import { getSelectedPlanUrl } from "@/utils";
import { emitUserInteractionEvent } from "@/observability/tracking/avroEventTracking/emitUserInteractionEvent";
import { Feature } from "./Feature";
import { PlanTitle } from "@/components/PricingPage/PlanSummary/PlanTitle";

import {
  EventAction,
  InteractionName,
  OptionSelected,
} from "@/observability/tracking/avroEventTracking/trackingTypes";

import { styles } from "./PlanCard.style";

const getIdentityFromQuerystring = () => {
  const identity = Router?.router?.query?.identity;
  return Array.isArray(identity) ? identity[identity.length - 1] : identity;
};

const addIdentity = (url: string) => {
  const identity = getIdentityFromQuerystring();
  return identity ? addParamsToUrl(url, { identity }) : url;
};

export type PlanCardProps = {
  currency: Currency;
  description?: string;
  features?: FeatureListing[];
  previousPlanType?: string | null;
  primaryCTA?: { color?: ButtonProps["color"]; text?: string; href?: string };
  secondaryCTA?: { text?: string; href?: string };
  renderPriceDetails: () => React.ReactNode;
  tooltipPosition?: TooltipPosition;
  selectedPlan: {
    type: string;
    name: string;
  };
  tag?: string;
  footerLink?: { text: string; href: string };
};

export const PlanCard = ({
  currency,
  description,
  features = [],
  previousPlanType,
  primaryCTA = {},
  secondaryCTA = {},
  renderPriceDetails = () => null,
  selectedPlan,
  tooltipPosition,
  tag,
  footerLink,
}: PlanCardProps) => {
  const hasFeatures = features.length > 0;
  const primaryCTAHref =
    primaryCTA.href ||
    addIdentity(getSelectedPlanUrl(selectedPlan.type, currency.name));

  const handlePrimaryCTAClick = () => {
    emitUserInteractionEvent({
      destination_url_address: primaryCTAHref,
      event_action: EventAction.click,
      interaction_name: InteractionName.call_to_action,
      interaction_raw_text: primaryCTA.text || "Try free",
      option_selected: OptionSelected.cta_button,
      custom_tracking_fields: {
        plan_type: selectedPlan.type,
      },
    });
  };

  const handleSecondaryCTAClick = () => {
    emitUserInteractionEvent({
      destination_url_address: secondaryCTA.href,
      event_action: EventAction.click,
      interaction_name: InteractionName.call_to_action,
      interaction_raw_text: secondaryCTA.text,
      option_selected: OptionSelected.cta_button,
      custom_tracking_fields: {
        plan_type: selectedPlan.type,
      },
    });
  };

  const handleFooterLinkClick = () => {
    emitUserInteractionEvent({
      event_action: EventAction.click,
      interaction_name: InteractionName.plan_card_footer_link_clicked,
      interaction_raw_text: footerLink?.text,
      destination_url_address: footerLink?.href,
    });
  };

  return (
    <div
      css={[styles.root, hasFeatures && styles.tableFeatureRoot]}
      id={`plan-card-${selectedPlan.type}`}
    >
      {tag && (
        <span css={styles.tag}>
          <TagLabel color="sky" size="small">
            {tag}
          </TagLabel>
        </span>
      )}
      <div css={styles.planOverviewWrapper}>
        <div css={styles.planOverview}>
          <PlanTitle
            description={description}
            title={selectedPlan.type ? selectedPlan.type : selectedPlan.name}
            showCurrentTrialBadge={false}
          />
          <div css={styles.priceDetails}>{renderPriceDetails()}</div>
          <div css={styles.primaryCtaWrapper}>
            <Button
              color={primaryCTA.color}
              hasShadow={!primaryCTA.color || primaryCTA.color === "primary"}
              isFullWidth={true}
              size="small"
              href={primaryCTAHref}
              onClick={handlePrimaryCTAClick}
            >
              {primaryCTA.text || "Try free"}
            </Button>
          </div>
          <div css={styles.secondaryCtaWrapper}>
            {primaryCTA.text && secondaryCTA.href && (
              <Text textAlign="center" type="smallPrint1">
                <Link
                  href={secondaryCTA.href}
                  onClick={handleSecondaryCTAClick}
                >
                  {secondaryCTA.text}
                </Link>
              </Text>
            )}
          </div>
          {hasFeatures && (
            <div css={styles.featuresWrapper}>
              <div>
                <p css={styles.featureTitle}>
                  {previousPlanType
                    ? `Everything in ${previousPlanType}, plus:`
                    : `${selectedPlan.type} plan features:`}
                </p>
              </div>
              {features.map((feature) => (
                <Feature
                  key={feature.name}
                  feature={feature}
                  tooltipPosition={tooltipPosition}
                />
              ))}
            </div>
          )}
          {footerLink && (
            <div css={styles.footerLinkWrapper}>
              <Link href={footerLink.href} onClick={handleFooterLinkClick}>
                {footerLink.text}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
